import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScheduling, useDeletePatient, useBatchDeletePatients } from "./queries";
import { useAuth } from "@features/auth/AuthContext";
import { useDebouncedValue } from "@shared/hooks/useDebouncedValue";
import { Pagination } from "@shared/ui/Pagination";
import { HttpError } from "@shared/http/client";
import { ConfirmDialog } from "@shared/ui/ConfirmDialog";
import type { Patient } from "@/types/patient";

const PAGE_SIZE = 50;

export function PendientesPage() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const deleteMut = useDeletePatient();
  const batchDeleteMut = useBatchDeletePatients();
  const [toDelete, setToDelete] = useState<Patient | null>(null);
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const { data, isLoading, error, isFetching } = useScheduling({
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const canDelete   = hasRole("Administrador", "Programador");
  const canEvaluate = hasRole("Administrador", "Evaluador");
  const list = data?.items ?? [];
  const total = data?.total ?? 0;
  const allIds = useMemo(() => list.map((p) => p.id), [list]);
  const allSelected = selected.size > 0 && selected.size === allIds.length && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const canCarga = hasRole("Administrador", "Programador");

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>
          Pacientes pendientes
          {total > 0 && <span className="badge badge-blue ml-8">{total}</span>}
        </h1>
        <p>Pacientes programados a valorar por el equipo clínico.</p>
      </div>

      <div className="filter-row mt-16">
        <input
          type="text"
          placeholder="Buscar por nombre, documento o cirugía…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="filter-search"
        />
        {debouncedSearch && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSearch("")}>Limpiar</button>
        )}
        {canEvaluate && (
          <button type="button" className="btn btn-sm btn-primary ml-auto" onClick={() => navigate("/nueva")}>
            ➕ Nueva valoración
          </button>
        )}
        {canCarga && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate("/carga")}>
            📥 Carga Excel
          </button>
        )}
      </div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">
          Error cargando pacientes: {error.message}
        </div>
      )}

      {(isLoading || isFetching) && <div className="text-muted mt-16">{isLoading ? "Cargando…" : "Actualizando…"}</div>}

      {!isLoading && total === 0 && (
        <div className="text-muted mt-24">
          {debouncedSearch
            ? `Sin resultados para "${debouncedSearch}".`
            : <>No hay pacientes pendientes. Usa <a href="/carga" onClick={(e) => { e.preventDefault(); navigate("/carga"); }} className="link-brand">Carga Excel</a> para agregar.</>}
        </div>
      )}

      {list.length > 0 && (
        <>
          {canDelete && selected.size > 0 && (
            <div className="bulk-actions mt-16">
              <span><b>{selected.size}</b> seleccionado(s)</span>
              <button
                type="button"
                className="btn btn-sm btn-danger ml-8"
                onClick={() => setConfirmBatch(true)}
                disabled={batchDeleteMut.isPending}
              >
                Eliminar seleccionados ({selected.size})
              </button>
              <button type="button" className="btn btn-sm btn-secondary ml-8" onClick={() => setSelected(new Set())}>
                Limpiar selección
              </button>
            </div>
          )}

          <div className="table-scroll mt-16">
            <table className="data-table">
              <thead>
                <tr>
                  {canDelete && (
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={toggleAll}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                  )}
                  <th>Documento</th><th>Nombre</th><th>Cirugía</th><th>Fecha</th>
                  <th>Teléfono</th><th>Programador</th>
                  <th style={{ textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className={selected.has(p.id) ? "row-selected" : ""}>
                    {canDelete && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                          aria-label={`Seleccionar ${p.nombre}`}
                        />
                      </td>
                    )}
                    <td>{p.doc}</td>
                    <td>{p.nombre}</td>
                    <td>{p.cirugia}</td>
                    <td>{p.fecha}</td>
                    <td>{p.telefono}</td>
                    <td>{p.programador}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canEvaluate && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/nueva?doc=${encodeURIComponent(p.doc)}`)}
                        >
                          Valorar
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger ml-8"
                          onClick={() => setToDelete(p)}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} total={total} limit={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <ConfirmDialog
        open={!!toDelete}
        title="Eliminar paciente"
        message={
          toDelete
            ? `Se eliminará a ${toDelete.nombre} (doc ${toDelete.doc}) de la programación. Esta acción queda registrada en auditoría.`
            : ""
        }
        confirmLabel={deleteMut.isPending ? "Eliminando…" : "Eliminar"}
        tone="danger"
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          if (!toDelete) return;
          deleteMut.mutate(toDelete.id, { onSettled: () => setToDelete(null) });
        }}
      />

      <ConfirmDialog
        open={confirmBatch}
        title={`Eliminar ${selected.size} paciente(s)`}
        message={`Se eliminarán ${selected.size} pacientes pendientes de la programación. La acción queda registrada en auditoría con tu usuario. ¿Continuar?`}
        confirmLabel={batchDeleteMut.isPending ? "Eliminando…" : `Eliminar ${selected.size}`}
        tone="danger"
        onCancel={() => setConfirmBatch(false)}
        onConfirm={() => {
          batchDeleteMut.mutate(Array.from(selected), {
            onSettled: () => {
              setConfirmBatch(false);
              setSelected(new Set());
            },
          });
        }}
      />
    </div>
  );
}
